import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const architectureComparisonComponent: ComponentDef = {
  id: 'architecture-comparison',
  name: 'Architecture Comparison',
  description: 'Side-by-side skeleton of encoder-only, decoder-only, and encoder-decoder stacks with different comparison views.',
  category: 'component',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'd_ff', label: 'FFN dimension', type: 'number', default: 2048, min: 64, max: 16384, step: 64 },
    {
      name: 'variant',
      label: 'View',
      type: 'select',
      default: 'overview',
      options: [
        { label: 'Overview', value: 'overview' },
        { label: 'Attention Choices', value: 'attention' },
        { label: 'FFN-MoE', value: 'ffn_moe' },
        { label: 'Training & Scale', value: 'training' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const d_ff = Number(params.d_ff);
    const variant = String(params.variant || 'overview');

    const g = new GraphBuilder();

    if (variant === 'attention') {
      // Attention Choices: compare self-attention, cross-attention, and causal masked attention
      g.input('enc_in', [seq_len, d_model]);
      g.matmul('self_attn', 'enc_in', 'enc_in', [seq_len, seq_len]);
      g.softmax('self_probs', 'self_attn', -1);

      g.input('dec_in', [seq_len, d_model]);
      g.matmul('cross_qk', 'dec_in', 'enc_in', [seq_len, seq_len]);
      g.softmax('cross_probs', 'cross_qk', -1);

      g.input('causal_in', [seq_len, d_model]);
      g.matmul('causal_attn', 'causal_in', 'causal_in', [seq_len, seq_len]);
      g.mask('causal_mask', 'causal_attn');
      g.softmax('causal_probs', 'causal_mask', -1);

      g.add('cmp_1', 'self_probs', 'cross_probs');
      g.add('cmp_2', 'cmp_1', 'causal_probs');
      g.output('out', 'cmp_2');

    } else if (variant === 'ffn_moe') {
      // FFN-MoE: compare dense FFN vs MoE FFN (routed experts)
      g.input('enc_in', [seq_len, d_model]);
      g.linear('dense_up', 'enc_in', [d_model, d_ff]);
      g.activation('dense_act', 'dense_up', 'GELU');
      g.linear('dense_down', 'dense_act', [d_ff, d_model]);

      g.input('moe_in', [seq_len, d_model]);
      g.router('moe_router', 'moe_in', [4]);
      g.linear('expert_0', 'moe_in', [d_model, d_ff / 4]);
      g.linear('expert_1', 'moe_in', [d_model, d_ff / 4]);
      g.concat('expert_cat', ['expert_0', 'expert_1'], [seq_len, d_ff / 2]);
      g.gate('moe_out', 'expert_cat', [seq_len, d_ff / 2]);

      g.input('seq2seq_in', [seq_len, d_model]);
      g.linear('seq2seq_up', 'seq2seq_in', [d_model, d_ff]);
      g.activation('seq2seq_act', 'seq2seq_up', 'GELU');
      g.linear('seq2seq_down', 'seq2seq_act', [d_ff, d_model]);

      g.add('cmp_1', 'dense_down', 'moe_out');
      g.add('cmp_2', 'cmp_1', 'seq2seq_down');
      g.output('out', 'cmp_2');

    } else if (variant === 'training') {
      // Training & Scale: show loss computation and scaling factors
      g.input('enc_in', [seq_len, d_model]);
      g.scale('enc_scaled', 'enc_in', '1/sqrt(d_k)');
      g.linear('enc_up', 'enc_scaled', [d_model, d_ff]);
      g.linear('enc_down', 'enc_up', [d_ff, d_model]);
      g.dropout('enc_drop', 'enc_down', 0.1);

      g.input('dec_in', [seq_len, d_model]);
      g.scale('dec_scaled', 'dec_in', '1/sqrt(d_k)');
      g.linear('dec_up', 'dec_scaled', [d_model, d_ff]);
      g.linear('dec_down', 'dec_up', [d_ff, d_model]);
      g.dropout('dec_drop', 'dec_down', 0.1);

      g.input('seq2seq_in', [seq_len, d_model]);
      g.linear('seq2seq_enc', 'seq2seq_in', [d_model, d_ff]);
      g.linear('seq2seq_dec', 'seq2seq_enc', [d_ff, d_model]);

      g.crossEntropy('loss_val', 'enc_drop', 'dec_drop');
      g.loss('total_loss', 'loss_val', 'seq2seq_dec');
      g.metric('ppl', 'total_loss', 'perplexity');
      g.output('out', 'ppl');

    } else {
      // Overview (default): encoder-only, decoder-only, encoder-decoder skeletons
      g.input('enc_in', [seq_len, d_model]);
      g.linear('enc_up', 'enc_in', [d_model, d_ff]);
      g.activation('enc_act', 'enc_up', 'GELU');
      g.linear('enc_down', 'enc_act', [d_ff, d_model]);

      g.input('dec_in', [seq_len, d_model]);
      g.linear('dec_up', 'dec_in', [d_model, d_ff]);
      g.activation('dec_act', 'dec_up', 'GELU');
      g.linear('dec_down', 'dec_act', [d_ff, d_model]);
      g.mask('dec_mask', 'dec_down');

      g.input('seq2seq_in', [seq_len, d_model]);
      g.linear('seq2seq_enc', 'seq2seq_in', [d_model, d_ff]);
      g.activation('seq2seq_act', 'seq2seq_enc', 'GELU');
      g.linear('seq2seq_dec', 'seq2seq_act', [d_ff, d_model]);

      g.add('cmp_1', 'enc_down', 'dec_mask');
      g.add('cmp_2', 'cmp_1', 'seq2seq_dec');
      g.output('out', 'cmp_2');
    }

    return g.build();
  }
};
